import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { curriculumApi, WorldWithProgress } from '../../api/curriculumApi';
import { LockClosedIcon } from '@heroicons/react/24/solid';

/**
 * WorldMapPage Component
 * 
 * Displays all available worlds in a visual journey format.
 * Shows progress, locks worlds based on completion, and allows navigation to world details.
 */
const WorldMapPage: React.FC = () => {
  const navigate = useNavigate();
  const [worlds, setWorlds] = useState<WorldWithProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWorlds();
  }, []);

  const loadWorlds = async () => {
    try {
      setLoading(true);
      const data = await curriculumApi.getWorlds();
      setWorlds(data);
    } catch (err) {
      console.error('Failed to load worlds:', err);
      setError('Failed to load worlds. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getProgressPercentage = (world: WorldWithProgress): number => {
    if (!world.total_levels || world.total_levels === 0) return 0;
    return Math.round(((world.completed_levels || 0) / world.total_levels) * 100);
  };

  const isWorldLocked = (world: WorldWithProgress, index: number): boolean => {
    // All worlds are unlocked - users can access any content
    return false;
  };

  const getWorldStatus = (world: WorldWithProgress, index: number): string => {
    const locked = isWorldLocked(world, index);
    if (locked) return 'LOCKED';
    
    const progress = getProgressPercentage(world);
    if (progress === 100) return 'COMPLETED';
    if (progress > 0) return 'IN PROGRESS';
    
    return 'NOT STARTED';
  };

  const handleWorldClick = (world: WorldWithProgress, index: number) => {
    if (isWorldLocked(world, index)) {
      return; // Don't navigate if locked
    }
    navigate(`/learn/world/${world.id}`);
  };

  const getNextLevel = (world: WorldWithProgress): string | null => {
    if (!world.total_levels) return null;
    
    const completed = world.completed_levels || 0;
    if (completed >= world.total_levels) {
      return null; // World completed
    }
    
    return `Level ${completed + 1}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-100">
        <div className="alert alert-error max-w-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100">
      {/* Header */}
      <div className="bg-base-200 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-base-content mb-4">
            Your Learning Journey
          </h1>
          <p className="text-center text-base-content/70 text-lg">
            Choose a world to continue your adventure
          </p>
        </div>
      </div>

      {/* Worlds Grid */}
      <div className="container mx-auto max-w-6xl px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {worlds.map((world, index) => {
            const progressPercent = getProgressPercentage(world);
            const status = getWorldStatus(world, index);
            const locked = isWorldLocked(world, index);
            const nextLevel = getNextLevel(world);

            return (
              <div
                key={world.id}
                onClick={() => handleWorldClick(world, index)}
                className={`card bg-base-200 shadow-xl transition-all duration-200 ${
                  locked 
                    ? 'opacity-60 cursor-not-allowed' 
                    : 'hover:shadow-2xl hover:scale-105 cursor-pointer'
                }`}
              >
                <div className="card-body">
                  {/* Icon and Title */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`text-5xl ${locked ? 'opacity-50' : ''}`}>
                      {locked ? <LockClosedIcon className="h-12 w-12 text-base-content/50" /> : world.icon}
                    </div>
                    <div className="flex-1">
                      <h2 className="card-title text-xl font-bold">
                        World {world.order}: {world.title}
                        {!locked && (
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-primary">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        )}
                      </h2>
                      <p className="text-sm text-base-content/70 mt-1">
                        {world.completed_levels || 0}/{world.total_levels || 0} levels
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-base-content/70">Progress</span>
                      <span className={`text-sm font-bold ${
                        status === 'IN PROGRESS' ? 'text-warning' :
                        status === 'COMPLETED' ? 'text-success' :
                        'text-base-content/50'
                      }`}>
                        {locked ? '0%' : `${progressPercent}%`}
                      </span>
                    </div>
                    <progress 
                      className={`progress w-full ${
                        locked ? 'progress-neutral' :
                        status === 'IN PROGRESS' ? 'progress-warning' :
                        status === 'COMPLETED' ? 'progress-success' :
                        'progress-primary'
                      }`}
                      value={locked ? 0 : progressPercent} 
                      max="100"
                    ></progress>
                  </div>

                  {/* Status and Next Level */}
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs text-base-content/60 mb-1">Status</div>
                      <div className={`badge ${
                        status === 'LOCKED' ? 'badge-neutral' :
                        status === 'IN PROGRESS' ? 'badge-warning' :
                        status === 'COMPLETED' ? 'badge-success' :
                        'badge-ghost'
                      } font-semibold`}>
                        {status}
                      </div>
                    </div>

                    {nextLevel && !locked && (
                      <div className="text-right">
                        <div className="text-xs text-base-content/60 mb-1">Next:</div>
                        <div className="text-sm font-bold text-primary">
                          {nextLevel}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {worlds.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🌍</div>
            <h3 className="text-2xl font-bold mb-2">No Worlds Available</h3>
            <p className="text-base-content/70">
              Check back soon for new learning content!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorldMapPage;
